import React, { useState, useEffect } from 'react';
import { Avatar, Container, Box, Stack, Card, CardContent, Typography, Grid, Button, TextField, CircularProgress } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PersonIcon from '@mui/icons-material/Person';
import { alpha } from '@mui/material/styles';
import toast from 'react-hot-toast';
import useSWR from 'swr';

import { userApi } from 'src/api/userApi';
import { profileApi } from 'src/api/profileApi';
import { processAvatarImage, formatFileSize } from 'src/utils/imageUtils';

const fetcher = () => userApi.getCurrentUserInfo();

const EditProfile = () => {
  const { data, mutate } = useSWR('currentUser', fetcher);

  const userDetails = data || null;

  const [avatar, setAvatar] = useState(userDetails?.avatar || '');
  const [avatarTemp, setAvatarTemp] = useState(null);
  const [firstName, setFirstName] = useState(userDetails?.firstName || '');
  const [lastName, setLastName] = useState(userDetails?.lastName || '');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setAvatar(userDetails?.avatar || '');
    setFirstName(userDetails?.firstName || '');
    setLastName(userDetails?.lastName || '');
  }, [userDetails]);

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];

    // Basic validation
    if (!file) {
      toast.error('Please select an image');
      return;
    }

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Clean up previous preview if exists
    if (avatarTemp) {
      URL.revokeObjectURL(avatarTemp);
    }

    setIsProcessing(true);

    try {
      // Process the image (resize, compress, validate)
      const result = await processAvatarImage(file);

      if (!result.success) {
        toast.error(result.error);
        setIsProcessing(false);
        return;
      }

      // Show warning if any (e.g., animated GIF)
      if (result.warning) {
        toast(result.warning, { icon: '⚠️', duration: 4000 });
      }

      // Show processing info if image was modified
      if (result.wasProcessed) {
        toast.success(
          `Image optimized: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.newSize)}`,
          { duration: 3000 }
        );
      }

      // Set preview
      setAvatarTemp(result.previewUrl);

      // Attempt to upload the processed file
      const response = await profileApi.uploadAvatar(result.file);

      if (response.error === false) {
        toast.success('Avatar updated successfully');
        // Refresh user data to get the new avatar URL from server
        mutate();
        // Keep the preview visible until new data loads
        // The preview URL will be cleaned up when component unmounts or new image is selected
      } else {
        throw new Error(response.status || 'Upload failed');
      }
    } catch (error) {
      toast.error(`Upload failed: ${error.message}`);
      // Clean up preview on error (avatarTemp was set from result.previewUrl)
      if (avatarTemp) {
        URL.revokeObjectURL(avatarTemp);
      }
      setAvatarTemp(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleError = (error) => {
    toast.error(error.message || 'Something went wrong');
    toast.error('Please contact support@dreamlinked.ca');
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    try {
      const response = await profileApi.updateUserInfo(firstName, lastName);

      if (!response.error) {
        toast.success('Profile updated!', { duration: 4000 });

        mutate();
      } else {
        toast.error('Required info missing');
      }
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Container sx={{ minHeight: 'calc(100vh - 100px)', my: 6 }}>
      <form onSubmit={handleFormSubmit}>
        <Typography variant="h4">Edit Profile</Typography>
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={12}>
                <Stack alignItems="center" direction="row" spacing={2}>
                  <Box
                    sx={{
                      borderColor: 'neutral.300',
                      borderRadius: '50%',
                      borderStyle: 'dashed',
                      borderWidth: 1,
                      p: '4px',
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: '50%',
                        height: '100%',
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      <label htmlFor="avatar-upload-input">
                        <Box
                          sx={{
                            alignItems: 'center',
                            backgroundColor: (theme) => alpha(theme.palette.neutral[700], 0.5),
                            borderRadius: '50%',
                            color: 'common.white',
                            cursor: isProcessing ? 'wait' : 'pointer',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                            left: 0,
                            opacity: isProcessing ? 1 : 0,
                            position: 'absolute',
                            top: 0,
                            width: '100%',
                            zIndex: 1,
                            '&:hover': {
                              opacity: 1,
                            },
                          }}
                        >
                          {isProcessing ? (
                            <CircularProgress size={24} sx={{ color: 'common.white' }} />
                          ) : (
                            <Stack alignItems="center" direction="row" spacing={1}>
                              <CameraAltIcon />
                              <Typography
                                color="inherit"
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                }}
                              >
                                Select
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                        <Avatar src={avatarTemp || avatar} sx={{ height: 100, width: 100 }}>
                          <PersonIcon />
                        </Avatar>
                      </label>
                      <input
                        id="avatar-upload-input"
                        type="file"
                        accept="image/*"
                        hidden
                        disabled={isProcessing}
                        onChange={handleAvatarChange}
                        data-testid="avatar-upload-input"
                      />
                    </Box>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  fullWidth
                  value={firstName ?? ''}
                  label="First Name"
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  sx={{
                    flexGrow: 1,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  fullWidth
                  value={lastName ?? ''}
                  label="Last Name"
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  sx={{
                    flexGrow: 1,
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Button color="primary" type="submit" variant="contained" sx={{ mt: 4 }}>
          Save Changes
        </Button>
      </form>
    </Container>
  );
};

export default EditProfile;
